import { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Copy, Mail, MessageSquare, Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function ShareScreen({
  url,
  vehicleLabel,
  otherRoleLabel,
  agreementId,
  prefillEmail = "",
  alreadySentTo = null,
  onSent = () => {},
}) {
  const [copied, setCopied] = useState(false);
  const [otherEmail, setOtherEmail] = useState(prefillEmail || "");
  const [otherPhone, setOtherPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState(alreadySentTo);
  const autoTriedRef = useRef(false);

  const msg = `Please review and sign the vehicle bill of sale for ${vehicleLabel}. ${url}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy");
    }
  };

  const sendInvite = async (emailOverride) => {
    const target = (emailOverride ?? otherEmail).trim();
    if (!target || !target.includes("@")) {
      toast.error("Enter a valid email address");
      return false;
    }
    if (!agreementId) return false;
    setSending(true);
    try {
      const r = await api.emailSigningLink(agreementId, target);
      if (r?.ok) {
        setSentTo(target);
        toast.success(`Signing invitation sent to ${target}`);
        onSent(target);
        return true;
      }
      toast.error("Could not send email — copy the link manually below");
      return false;
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Could not send email";
      toast.error(detail);
      return false;
    } finally {
      setSending(false);
    }
  };

  // Auto-send the invitation once if a prefill email was captured during the
  // wizard and we have not sent yet. We only attempt this once per mount.
  useEffect(() => {
    if (autoTriedRef.current) return;
    if (!prefillEmail || sentTo) return;
    autoTriedRef.current = true;
    sendInvite(prefillEmail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillEmail, sentTo]);

  const mailto = otherEmail
    ? `mailto:${encodeURIComponent(otherEmail)}?subject=${encodeURIComponent("Review & sign vehicle bill of sale")}&body=${encodeURIComponent(msg)}`
    : `mailto:?subject=${encodeURIComponent("Review & sign vehicle bill of sale")}&body=${encodeURIComponent(msg)}`;
  const smsHref = otherPhone ? `sms:${otherPhone}?&body=${encodeURIComponent(msg)}` : `sms:?&body=${encodeURIComponent(msg)}`;
  const whatsappHref = `https://wa.me/${otherPhone.replace(/[^\d]/g, "")}?text=${encodeURIComponent(msg)}`;

  return (
    <div className="space-y-5" data-testid="share-screen">
      <div className="csd-card p-6 bg-gradient-to-br from-white to-[var(--csd-primary-light)]/40">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--csd-primary)] grid place-items-center text-white shrink-0">
            <Send size={18} />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-lg">Send to the {otherRoleLabel}</h3>
            <p className="text-sm text-[var(--csd-text-secondary)] mt-1">
              Share this secure link. The {otherRoleLabel} fills their info, signs, and both parties receive the final PDF by email.
            </p>
          </div>
        </div>
      </div>

      {/* AUTO-EMAIL CONFIRMATION */}
      {sentTo && (
        <div
          className="csd-card p-4 bg-[var(--csd-primary-light)]/40 border-[var(--csd-primary-light)]"
          data-testid="share-sent-banner"
        >
          <div className="flex items-start gap-3">
            <CheckCircle2 className="text-[var(--csd-primary)] mt-0.5" size={20} />
            <div className="text-sm">
              <div className="font-semibold text-[var(--csd-text-primary)]">
                Signing invitation sent to {sentTo}
              </div>
              <div className="text-[var(--csd-text-secondary)] mt-0.5">
                We emailed the {otherRoleLabel} a secure link to review and sign. They have no account to create.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SEND BY EMAIL — primary action */}
      <div className="csd-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <Mail size={16} className="text-[var(--csd-primary)]" />
          <h4 className="font-semibold text-sm">
            {sentTo ? "Resend the invitation" : `Email the signing link to the ${otherRoleLabel}`}
          </h4>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            placeholder={`${otherRoleLabel}@example.com`}
            value={otherEmail}
            onChange={(e) => setOtherEmail(e.target.value)}
            className="h-12"
            data-testid="share-email-input"
          />
          <Button
            type="button"
            onClick={() => sendInvite()}
            disabled={sending}
            className="csd-btn-primary h-12 rounded-xl whitespace-nowrap"
            data-testid="share-send-btn"
          >
            {sending ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Send size={14} className="mr-2" />
                {sentTo ? "Resend" : "Send invitation"}
              </>
            )}
          </Button>
        </div>
        <p className="text-xs text-[var(--csd-text-secondary)] mt-3">
          Sent from <span className="font-mono">noreply@carsaledoc.com</span> — please ask the {otherRoleLabel} to check spam if it doesn't arrive.
        </p>
      </div>

      {/* DIRECT LINK */}
      <div className="space-y-2">
        <label className="text-sm font-semibold">Or copy the secure link</label>
        <div className="flex gap-2">
          <Input data-testid="share-link-input" readOnly value={url} className="font-mono text-xs h-12" onClick={(e) => e.target.select()} />
          <Button type="button" onClick={copy} variant="outline" className="h-12 rounded-xl" data-testid="share-copy-btn">
            {copied ? <CheckCircle2 size={14} className="mr-2" /> : <Copy size={14} className="mr-2" />}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
      </div>

      {/* OTHER CHANNELS */}
      <div>
        <label className="text-sm font-semibold mb-2 block">Share another way</label>
        <div className="grid sm:grid-cols-2 gap-2 mb-3">
          <Input
            type="tel"
            placeholder={`${otherRoleLabel}'s phone (for SMS / WhatsApp)`}
            value={otherPhone}
            onChange={(e) => setOtherPhone(e.target.value)}
            data-testid="share-phone-input"
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <a href={mailto} data-testid="share-mailto" target="_blank" rel="noreferrer">
            <Button type="button" variant="outline" className="w-full rounded-xl h-12"><Mail size={14} className="mr-2" />Open in mail app</Button>
          </a>
          <a href={smsHref} data-testid="share-sms" target="_blank" rel="noreferrer">
            <Button type="button" variant="outline" className="w-full rounded-xl h-12"><MessageSquare size={14} className="mr-2" />SMS / iMessage</Button>
          </a>
          <a href={whatsappHref} data-testid="share-whatsapp" target="_blank" rel="noreferrer">
            <Button type="button" variant="outline" className="w-full rounded-xl h-12">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="mr-2"><path d="M17.6 6.32A7.85 7.85 0 0 0 12.05 4a7.94 7.94 0 0 0-6.84 11.91L4 20l4.18-1.1a7.94 7.94 0 0 0 3.86 1h.01a7.94 7.94 0 0 0 7.93-7.94 7.88 7.88 0 0 0-2.38-5.64Zm-5.55 12.21h-.01a6.59 6.59 0 0 1-3.37-.92l-.24-.14-2.49.65.66-2.42-.16-.25a6.6 6.6 0 1 1 12.27-3.49 6.6 6.6 0 0 1-6.66 6.57Z"/></svg>
              WhatsApp
            </Button>
          </a>
        </div>
      </div>

      <div className="text-xs text-[var(--csd-text-secondary)] leading-relaxed bg-gray-50 border border-[var(--csd-border)] rounded-xl p-4">
        The link is single-use for the {otherRoleLabel}. Anyone with this link can view and sign as the {otherRoleLabel}.
        Once they sign, the agreement is locked and a PDF is emailed to both parties.
      </div>
    </div>
  );
}
